import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Stack } from '@mui/material';

import { Modal } from 'components/Modal';
import { Checkbox, FormProvider, TextField } from 'components/Form';
import { RadioGroupField } from 'components/Form/RadioGroupField';
import useYupValidationResolver from 'hook/useYupValidationResolver';

import { useAnswerQuestion } from '../api/answerQuestion';
import { answerValidationSchema } from '../validation';
import { AttachmentsField, PublishQuestionFilesField, idsNotIn } from './AttachmentFields';
import { InfoRow, ModalActions, textAreaSx } from './ModalActions';

const defaultValues = (question) => ({
  publish: false,
  editQuestion: !!question?.revised_question,
  revisedQuestion: question?.revised_question ?? question?.question ?? '',
  answer: question?.answer ?? '',
  visibility: question?.visibility ?? '',
  files: [],
  keptFiles: question?.answer_files ?? [],
  publishedQuestionFileIds: (question?.question_files ?? [])
    .filter((file) => file.public !== false)
    .map((file) => file.file_id),
});

export const AnswerQuestionModal = ({ resource, id, question, open, handleClose }) => {
  const methods = useForm({
    defaultValues: defaultValues(question),
    resolver: useYupValidationResolver(answerValidationSchema),
  });
  const { mutate: answerQuestion, isPending } = useAnswerQuestion(resource, id, handleClose);

  const { reset, watch, setValue, handleSubmit } = methods;
  const editQuestion = watch('editQuestion');

  useEffect(() => {
    if (open) reset(defaultValues(question));
  }, [open, question, reset]);

  const onSubmit = ({
    publish,
    editQuestion,
    revisedQuestion,
    answer,
    visibility,
    files,
    keptFiles,
    publishedQuestionFileIds,
  }) =>
    answerQuestion({
      questionId: question.question_id,
      revised_question: editQuestion ? revisedQuestion : null,
      answer,
      visibility,
      publish,
      files,
      remove_file_ids: idsNotIn(
        question.answer_files,
        keptFiles.map((file) => file.file_id)
      ),
      hidden_question_file_ids: idsNotIn(question.question_files, publishedQuestionFileIds),
    });

  const submitAs = (publish) => () => {
    setValue('publish', publish);
    handleSubmit(onSubmit)();
  };

  const visibilityOptions = [
    { value: 'public', label: 'Public (all suppliers)' },
    {
      value: 'limited',
      label: `Limited (only ${question?.company_name ?? 'the asking supplier'})`,
    },
  ];

  return (
    <Modal
      title="Answer question"
      open={open}
      handleClose={handleClose}
      sx={{ width: 600, overflowY: 'auto' }}
    >
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <Stack gap={1.5} mb={3}>
          <InfoRow label="Supplier" value={question?.company_name} note="Anonymized to suppliers" />
          <InfoRow
            label="Supplier user"
            value={question?.asked_by_name}
            note="Anonymized to suppliers"
          />
          <InfoRow label="Question" value={question?.question} />
          <PublishQuestionFilesField
            name="publishedQuestionFileIds"
            files={question?.question_files}
          />
          <Checkbox name="editQuestion" label="Edit question before publishing" />
          {editQuestion && (
            <TextField
              fullWidth
              name="revisedQuestion"
              label="Edited question"
              helperText="Suppliers see the edited question instead of the original"
              multiline
              minRows={3}
              required
              sx={textAreaSx}
            />
          )}
          <TextField
            fullWidth
            name="answer"
            label="Answer"
            placeholder="Please enter your answer"
            multiline
            minRows={5}
            required
            sx={textAreaSx}
          />
          <RadioGroupField
            name="visibility"
            label="Visibility"
            options={visibilityOptions}
            required
          />
          <AttachmentsField keptName="keptFiles" />
        </Stack>
        <ModalActions
          onCancel={handleClose}
          onDraft={submitAs(false)}
          onSubmit={submitAs(true)}
          disabled={isPending}
        />
      </FormProvider>
    </Modal>
  );
};
